<script lang="ts">
	import classNames from 'classnames';
	import { ShopRoutes } from '../../constants/routes';
	import { HorizontalScrollControls } from '#lib/components/HorizontalScrollControls/index.js';
	import { ALL } from '#lib/features/checkout/utils/getCartIconContext/index.js';
	import { getCheckoutContext } from '#lib/features/checkout/utils/getCheckoutContext/index.js';
	import { CartNavBar } from '#lib/features/navigation/components/CartNavBar/index.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { GlobalSearch } from '#lib/features/search/global/components/GlobalSearch/index.js';
	import {
		getContentWidth,
		horizontalScrollHint,
		initialHorizontalScrollState,
		shouldShowScrollControls,
		type HorizontalScrollState,
	} from '#lib/utils/horizontalScrollHint/index.js';
	import type { GetShopCategoriesQuery } from '#lib/websites/shop/queries/__generated__/getShopCategories.generated.js';
	import { page } from '$app/state';
	import { HeniShopLogo } from '$global/assets/logos/HeniShopLogo';
	import { Container } from '$global/components/Container';
	import { IconLink } from '$global/components/IconLink';
	import { Txt } from '$global/components/Txt';
	import { SearchPageContainer } from '$global/features/search/components/SearchPageContainer';
	import { createSearchCombobox } from '$global/features/search/utils/createSearchCombobox';
	import { getPageData } from '$global/utils/getPageData/getPageData';
	import { isElementVisibleInViewport } from '$global/utils/isElementVisibleInViewport/isElementVisibleInViewport';
	import type { ShopPageData } from '$routes/shop/types';

	interface Props {
		children?: import('svelte').Snippet;
	}

	type ShopCategoryRelation = NonNullable<
		NonNullable<GetShopCategoriesQuery['Shop_Homepage']>['categories']
	>[number];
	type ShopCategory = NonNullable<
		NonNullable<ShopCategoryRelation>['Shop_Category_id']
	>;

	let data = $derived(getPageData<ShopPageData>(page.data));
	let shopCategories = $derived(
		data.shopCategories as ShopCategory[] | undefined
	);
	let { children }: Props = $props();
	let ref: HTMLDivElement | null = $state(null);
	let navigationState: HorizontalScrollState = $state(
		initialHorizontalScrollState
	);

	// The category cells fit on one line whenever the viewport is at least the
	// sum of their natural widths (no gap) plus the Container's horizontal
	// padding. Below that width they overflow and the scroll arrows are shown.
	// `contentWidth` (XX) is measured from the real DOM on mount and whenever the
	// category list changes, so it adapts to whatever categories the CMS returns
	// rather than a hardcoded breakpoint.
	const CONTAINER_PADDING_PX = 48; // Container lg:px-6 = 24px each side
	let contentWidth = $state(0);
	let viewportWidth = $state(0);

	const measureContentWidth = () => {
		if (!ref) return;
		const cells = Array.from(ref.children) as HTMLElement[];
		contentWidth = getContentWidth(cells, CONTAINER_PADDING_PX);
	};

	// Arrows show only while the content can't fit on one line at the current
	// viewport width (the width rule is the authority for whether the controls
	// appear at all). We keep the measured `atStart`/`atEnd` from
	// `horizontalScrollHint` — they decide WHICH chevron shows mid-scroll — but
	// override `canScroll` with the width rule so the controls' own visibility
	// gate follows it exactly (until content is measured, fall back to the
	// measured overflow so nothing flickers on first paint).
	let canScrollByWidth = $derived(
		shouldShowScrollControls({
			contentWidth,
			viewportWidth,
			fallback: navigationState.canScroll,
		})
	);
	let controlsState = $derived({
		...navigationState,
		canScroll: canScrollByWidth,
	});

	const {
		checkoutForm: { clearForm, form },
	} = getCheckoutContext();

	$effect(() => {
		if (ref) {
			if (!$form[ALL]) {
				clearForm();
			}

			const activeLink = links.find((link) => link.active);
			if (!activeLink) {
				return;
			}

			const elt = document.getElementById(
				`shop-category-${activeLink.label.toLowerCase()}`
			);

			if (elt && !isElementVisibleInViewport(elt)) {
				ref.scrollLeft = elt.offsetLeft;
			}
		}
	});

	// Track the viewport width, and measure the category content width on mount +
	// whenever the list changes. Referencing `links.length` inside the effect
	// makes it re-measure after categories load/change (once the DOM has
	// updated). $effect never runs during SSR, so `window` is safe here.
	$effect(() => {
		// re-run when the number of category cells changes
		void links.length;
		viewportWidth = window.innerWidth;
		const onResize = () => {
			viewportWidth = window.innerWidth;
			measureContentWidth();
		};
		window.addEventListener('resize', onResize);
		// Measure after the browser has laid out the (possibly just-updated) cells.
		const frame = requestAnimationFrame(measureContentWidth);
		return () => {
			window.removeEventListener('resize', onResize);
			cancelAnimationFrame(frame);
		};
	});

	const combobox = createSearchCombobox();

	let pathname = $derived(page.url.pathname);

	let links = $derived([
		{
			label: 'All',
			href: ShopRoutes.All,
			active: pathname === ShopRoutes.All,
		},
		...(shopCategories ?? [])
			.flatMap((category) => (category ? [category] : []))
			.map((category) => ({
				label: category.title,
				href:
					category.slug === 'books'
						? ShopRoutes.Books
						: `${ShopRoutes.Merch}/${category.slug}`,
				active:
					category.slug === 'books'
						? page.url.pathname === ShopRoutes.Books
						: page.params.slug === category.slug,
			})),
		{
			label: 'artists & authors',
			href: ShopRoutes.ArtistsAndAuthors,
			active: pathname === ShopRoutes.ArtistsAndAuthors,
		},
		{
			label: 'Collections',
			href: ShopRoutes.Collections,
			active: pathname === ShopRoutes.Collections,
		},
	]);

	const dataCyPrefix = 'shop-header';
</script>

<!--
	The shop keeps its whole header pinned: global bar, shop sub-nav (cart /
	search) and the category scroller all stay put while scrolling, so the
	scroller never floats over page content on its own.
-->
<CartNavBar
	{combobox}
	SearchComponent={GlobalSearch}
	isSearchEnabled={true}
	classes={{ bottomBar: 'sticky top-12 !z-20 lg:border-b-0' }}
>
	{#snippet homeIcon()}
		<IconLink
			dataCy={dataCyPrefix}
			href={GlobalRoutes.ShopHome}
			label="See HENI Shop home page"
			class="outline-none"
		>
			<HeniShopLogo class="xs:scale-100 origin-left scale-75" />
		</IconLink>
	{/snippet}
</CartNavBar>

<!--
	Same tab-chip system as the artist detail page sections, so moving between
	HENI sites feels like staying on one site. These stay real links (each chip
	is a route), and the whole row keeps the pinned-header arrangement above.
-->
<div
	class="sticky top-12 z-[11] w-full border-gray-200 bg-white py-0 lg:top-[6.5rem] lg:border-t"
>
	<Container dataCy={dataCyPrefix} class="px-0 sm:px-2 lg:px-6">
		<div class="relative">
			<div
				bind:this={ref}
				use:horizontalScrollHint={{
					onChange: (state) => (navigationState = state),
				}}
				class="horizontal-scroll-hint no-scrollbar flex w-full justify-between overflow-x-auto border-b border-gray-200"
			>
				{#each links as link}
					<a
						data-cy={`${dataCyPrefix}-menu-category-tag`}
						id={`shop-category-${link.label.toLowerCase()}`}
						href={link.href}
						class={classNames(
							'-mb-px flex shrink-0 items-center gap-2 border-b-2 px-4 py-3',
							link.active
								? 'border-gray-900'
								: 'border-transparent text-gray-500'
						)}
					>
						<Txt variant="body2" class="tracking-[.14em] uppercase">
							{link.label}
						</Txt>
					</a>
				{/each}
			</div>

			<HorizontalScrollControls
				target={ref}
				state={controlsState}
				previousLabel="Show previous shop categories"
				nextLabel="Show more shop categories"
			/>
		</div>
	</Container>
</div>

<SearchPageContainer {combobox}>
	{@render children?.()}
</SearchPageContainer>
